import Appearance from "./Appearance";
import CharacterClass from "./CharacterClass";
import Characteristics from "./Characteristics";
import Inventory from "./Inventory";
import Level from "./Level";
import Race from "./Race";
import Skills from "./Skills";
import SpellBook from "./SpellBook";
import Task from "./Task";
import { calculateArmorClass } from "../calculations/armorClass";
import { resolveAlignmentId } from "../catalogs/characterSystemCatalog";
import { reconcileCharacterRules } from "../calculations/reconcileCharacterRules";

class Character {
  constructor() {
    this.id = "";
    this.characterName = "";
    this.god = "";
    this.avatarUrl = "";
    this.alignmentId = "";
    this.background = "";
    this.backstory = "";
    this.affiliations = "";
    this.achievements = "";
    this.race = {};
    this.characterClass = [];
    this.appearance = {};
    this.level = {};
    this.characteristics = {};
    this.tasks = [];
    this.skills = {};
    this.passives = [];
    this.feats = [];
    this.spellBook = {};
    this.inventory = {};
    this.grantedFeatures = [];
    this.ruleChoices = [];
    this.ruleDerived = {};
  }
}

export function mapJsonToCharacter(jsonData) {
  const character = new Character();
  const data = jsonData?.json ?? {};

  character.id = jsonData?.id ?? "";
  character.characterName = jsonData?.characterName ?? "";
  character.backstory = data.backstory ?? "";
  character.achievements = data.achievements ?? "";
  character.avatarUrl = data.avatarUrl ?? "";
  character.background = data.background ?? "";
  character.affiliations = data.affiliations ?? "";
  character.alignmentId = resolveAlignmentId(data.alignmentId || data.alignment);
  character.god = data.god ?? "";

  character.appearance = new Appearance(data.appearance ?? {});
  character.race = new Race(data.race ?? {});

  const characterClasses = Array.isArray(data.characterClass)
    ? data.characterClass
    : data.characterClass
      ? [data.characterClass]
      : [{ name: "", classLevel: 0, availableWeapons: "", availableArmor: "" }];
  character.characterClass = characterClasses.map((cls) => new CharacterClass(cls));
  character.level = new Level({ name: "", level: 0, currentXP: 0, neededXP: 0, ...data.level });
  character.characteristics = new Characteristics({
    mainCharacteristicList: [],
    hp: { currentHP: 0, maxHP: 0, additionalHP: 0 },
    armorClass: 0,
    initiative: 0,
    walkingDistance: 0,
    savingThrows: [],
    ...data.characteristics,
  });
  character.tasks = (data.tasks ?? []).map((taskJson) => new Task(taskJson));
  character.skills = new Skills({ skillsList: [], ...data.skills });
  character.passives = Array.isArray(data.passives)
    ? data.passives.map((passive) => ({ ...passive }))
    : [];
  character.feats = data.feats ?? [];
  character.spellBook = new SpellBook({ spells: [], ...data.spellBook });
  character.inventory = new Inventory({ money: [], items: [], ...data.inventory });
  character.grantedFeatures = Array.isArray(data.grantedFeatures) ? data.grantedFeatures.map((entry) => ({ ...entry })) : [];
  character.ruleChoices = Array.isArray(data.ruleChoices) ? data.ruleChoices.map((entry) => ({ ...entry })) : [];
  character.ruleDerived = data.ruleDerived && typeof data.ruleDerived === "object" ? { ...data.ruleDerived } : {};
  character.characteristics.armorClass = calculateArmorClass(
    character.characteristics,
    character.inventory.items
  );

  return reconcileCharacterRules(character);
}

export function requiresCharacterIdMigration(jsonData) {
  const data = jsonData?.json ?? {};
  return Boolean(
    data.alignment ||
    data.race?.name ||
    (Array.isArray(data.characterClass) ? data.characterClass : [data.characterClass]).filter(Boolean).some((entry) => entry.name) ||
    data.characteristics?.mainCharacteristicList?.some((entry) => entry.name) ||
    data.characteristics?.pathfinderSaves?.some((entry) => entry.name || entry.abilityName) ||
    data.skills?.skillsList?.some((entry) => entry.mainCharacteristicName || (entry.id?.startsWith("default-") && entry.name)) ||
    data.inventory?.items?.some((entry) => /[A-Z ]/.test(entry.type || "") || /[A-Z ]/.test(entry.damageType || ""))
  );
}
export function toCharacterUpdateRequest(character) {
  const reconciled = reconcileCharacterRules(character);
  const { id, characterName, avatarUrl, ...json } = reconciled;

  return {
    characterName: characterName.trim(),
    json,
  };
}

export default Character;
